/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback } from 'react';

import Modal from '../../../../modal/modal';
import Typography from '../../../../typography/typography';
import DynamicInput from '../text';
import Button from '../../../../button/button';
import ModalLoader from '../../../../loaders/modal-loader';
import Toast from '../../../../toast/toast';
import { useTranslation } from 'react-i18next';

import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import DynamicSearchSelect from '../dynamic-select';
import DynamicSelect from '../select';
import { IModalComponentProps } from '../../index';
import { fetchOptions, skuGenerator } from '../../../../../utils/common';

import './fixed-asset-item-add-modal.scss';

interface IFormValues {
    inventory_fixed_asset_item: {
        name: string;
        sku: string;
        category: number | null;
        base_unit: number | null;
        company: number[];
        unit_of_measurement: number | null;
        stock_unit: number | null;
        purchase_unit: number | null;
        sales_unit: number | null;
        costing_method: string;
        depreciation_method: string;
        computation_method: string;
        default_tax_id: number | null;
        income_account_id: number | null;
        asset_account_id: number | null;
        cogs_account_id: number | null;
        fixed_asset_account_id: number | null;
        depreciation_account_id: number | null;
        declining_factor: number | null;
    };
}

interface IToast {
    type: 'normal' | 'alert';
    message: string;
}

const COSTING_METHODS = [
    { label: 'FIFO', value: 'FIFO' },
    { label: 'LIFO', value: 'LIFO' },
    { label: 'Weighted Average Cost', value: 'Weighted Average Cost' }
];

const DEPRECIATION_METHODS = [
    { label: 'Straight line', value: 'Straight line' },
    { label: 'Declining', value: 'Declining' }
];

const COMPUTATION_METHODS = [
    { label: 'Constant periods', value: 'Constant periods' },
    { label: 'Based on days per period', value: 'Based on days per period' }
];

const validationSchema = Yup.object().shape({
    inventory_fixed_asset_item: Yup.object().shape({
        name: Yup.string().required('Name is required'),
        sku: Yup.string().required('SKU is required'),
        category: Yup.mixed().required('Category is required'),
        base_unit: Yup.mixed().required('Base Unit is required'),
        company: Yup.array().min(1, 'At least one company is required').required('Company is required'),
        unit_of_measurement: Yup.mixed().required('Unit of Measurement is required'),
        stock_unit: Yup.mixed().nullable(),
        purchase_unit: Yup.mixed().nullable(),
        sales_unit: Yup.mixed().nullable(),
        costing_method: Yup.string().required('Costing Method is required'),
        depreciation_method: Yup.string().required('Depreciation Method is required'),
        computation_method: Yup.string().required('Computation Method is required'),
        default_tax_id: Yup.mixed().required('Default Tax is required'),
        income_account_id: Yup.mixed().required('Income Account is required'),
        asset_account_id: Yup.mixed().required('Asset Account is required'),
        cogs_account_id: Yup.mixed().required('COGS Account is required'),
        fixed_asset_account_id: Yup.mixed().required('Fixed Asset Account is required'),
        depreciation_account_id: Yup.mixed().required('Depreciation Account is required'),
        declining_factor: Yup.number()
            .nullable()
            .when('depreciation_method', {
                is: 'Declining',
                then: (schema) => schema.required('Declining Factor is required').min(0, 'Must be positive'),
                otherwise: (schema) => schema.nullable(),
            }),
    })
});

const FixedAssetItemAddModal: React.FC<IModalComponentProps> = ({
    isOpen,
    setIsOpen,
    modalProps,
    onSave,
    isLoading
}) => {
    const { t } = useTranslation();
    const [toast, setToast] = useState<IToast | null>(null);
    const [selectedCategoryData, setSelectedCategoryData] = useState<any>(null);
    const unitFields = ['stock_unit', 'purchase_unit', 'sales_unit', 'consumption_unit', 'base_unit'];
    const defaultValues = {
        inventory_fixed_asset_item: {
            name: '',
            sku: '',
            category: null,
            base_unit: null,
            company: [],
            unit_of_measurement: null,
            stock_unit: null,
            purchase_unit: null,
            sales_unit: null,
            costing_method: '',
            depreciation_method: '',
            computation_method: '',
            default_tax_id: null,
            income_account_id: null,
            asset_account_id: null,
            cogs_account_id: null,
            fixed_asset_account_id: null,
            depreciation_account_id: null,
            declining_factor: null,
            status: 1
        }
    }

    const methods = useForm<any>({
        mode: 'all',
        resolver: yupResolver(validationSchema),
        defaultValues: defaultValues,
    });
    const { control, handleSubmit, reset, setValue, trigger } = methods || {};

    // Watch name and category for auto-generating SKU
    const [watchedName, watchedCategory, watchedUom, watchedDepreciationMethod] = useWatch({
        control, name:
            ['inventory_fixed_asset_item.name',
                'inventory_fixed_asset_item.category',
                'inventory_fixed_asset_item.unit_of_measurement',
                'inventory_fixed_asset_item.depreciation_method']
    });


    // Auto-generate SKU when name or category changes
    useEffect(() => {
        if (watchedName && selectedCategoryData?.category_name) {
            const generatedSku = skuGenerator({
                name: watchedName,
                category: selectedCategoryData.category_name
            });
            setValue('inventory_fixed_asset_item.sku', generatedSku);
        }
    }, [watchedName, selectedCategoryData, setValue]);

    const handleModalClose = () => {
        reset(defaultValues);
        setSelectedCategoryData(null);
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        const isValid = await trigger();
        if (!isValid) return;
        await onSave({
            ...values.inventory_fixed_asset_item as any,
            type: 'inventory-fixed-asset',
            status: 1
        });
        handleModalClose();
    };

    const handleCategorySelect = useCallback((data: any) => {
        setSelectedCategoryData(data);
    }, []);

    const updateUomFields = useCallback(async () => {
        if (!watchedUom) return
        const filter = {
            '&template_id.eq': watchedUom,
            '&is_base_unit.eq': 1
        }
        const uomData = await fetchOptions('uom', filter, [], false)
        unitFields.forEach((item) => {
            setValue(`inventory_fixed_asset_item.${item}`, uomData?.id)
        })
    }, [watchedUom])
    useEffect(() => {
        updateUomFields()
    }, [updateUomFields])

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='md'
            fullWidth
            {...modalProps}>
            <>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {isLoading && (
                            <ModalLoader />
                        )}
                        <DialogTitle className='fixedAssetItemAddModal--DialogTitle'>
                            <Typography type='s3' color='inherit' weight='medium'>
                                {t("inventory.fixed_asset_items.modal.title", "Add Fixed Asset Item")}
                            </Typography>
                            <IconButton onClick={handleModalClose}>
                                <Close fontSize='small' />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent className='fixedAssetItemAddModal--DialogContents'>
                            <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                                {/* Name */}
                                <Grid item sm={6}>
                                    <DynamicInput
                                        type='text'
                                        formControl={control}
                                        label={t("inventory.fixed_asset_items.modal.name_label", "Name")}
                                        name='name'
                                        fieldArrayName='inventory_fixed_asset_item'
                                        placeholder={t("inventory.fixed_asset_items.modal.name_placeholder", "Enter Name")}
                                        required
                                    />
                                </Grid>

                                {/* SKU - Auto-generated */}
                                <Grid item sm={6}>
                                    <DynamicInput
                                        type='text'
                                        formControl={control}
                                        label={t("inventory.fixed_asset_items.modal.sku_label", "SKU")}
                                        name='sku'
                                        fieldArrayName='inventory_fixed_asset_item'
                                        placeholder={t("inventory.fixed_asset_items.modal.sku_placeholder", "Auto-generated")}
                                        required
                                        disabled
                                    />
                                </Grid>

                                {/* Category */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.category_label", "Category")}
                                        name="category"
                                        placeholder={t("inventory.fixed_asset_items.modal.category_placeholder", "Select Category")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='categoryItems'
                                        options={[]}
                                        required={true}
                                        getSelectedData={handleCategorySelect}
                                    />
                                </Grid>


                                {/* Company */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.company_label", "Company")}
                                        name="company"
                                        placeholder={t("inventory.fixed_asset_items.modal.company_placeholder", "Select Company")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='company'
                                        options={[]}
                                        is_multiselect={true}
                                        required={true}
                                    />
                                </Grid>

                                {/* Costing Method */}
                                <Grid item sm={6}>
                                    <DynamicSelect
                                        label={t("inventory.fixed_asset_items.modal.costing_method_label", "Costing Method")}
                                        name="costing_method"
                                        placeholder={t("inventory.fixed_asset_items.modal.costing_method_placeholder", "Select Costing Method")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        options={COSTING_METHODS}
                                        required={true}
                                    />
                                </Grid>

                                {/* Unit of Measurement Section Header */}
                                <Grid item xs={12} pt={2}>
                                    <Typography type='s3' weight='medium' color='theme.neutral.900'>
                                        {t("inventory.fixed_asset_items.modal.uom_section", "Unit of Measurement")}
                                    </Typography>
                                </Grid>

                                {/* UOM Template */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.unit_of_measurement_label", "UOM Template")}
                                        name="unit_of_measurement"
                                        placeholder={t("inventory.fixed_asset_items.modal.unit_of_measurement_placeholder", "Select UOM Template")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='uom_templates'
                                        required={true}
                                    />
                                </Grid>

                                {/* Stock Unit */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.stock_unit_label", "Stock Unit")}
                                        name="stock_unit"
                                        placeholder={t("inventory.fixed_asset_items.modal.stock_unit_placeholder", "Select Stock Unit")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='uom'
                                        customFilter={{
                                            '&template_id.eq': watchedUom || 0
                                        }}
                                    />
                                </Grid>

                                {/* Purchase Unit */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.purchase_unit_label", "Purchase Unit")}
                                        name="purchase_unit"
                                        placeholder={t("inventory.fixed_asset_items.modal.purchase_unit_placeholder", "Select Purchase Unit")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='uom'
                                        customFilter={{
                                            '&template_id.eq': watchedUom || 0
                                        }}
                                    />
                                </Grid>

                                {/* Sales Unit */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.sales_unit_label", "Consumption Unit")}
                                        name="sales_unit"
                                        placeholder={t("inventory.fixed_asset_items.modal.sales_unit_placeholder", "Select Sales Unit")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='uom'
                                        customFilter={{
                                            '&template_id.eq': watchedUom || 0
                                        }}
                                    />
                                </Grid>
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.sales_unit_label", "Consumption Unit")}
                                        name="consumption_unit"
                                        placeholder={t("inventory.fixed_asset_items.modal.sales_unit_placeholder", "Select Sales Unit")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='uom'
                                        customFilter={{
                                            '&template_id.eq': watchedUom || 0
                                        }}
                                    />
                                </Grid>
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.base_unit_label", "Base Unit")}
                                        name="base_unit"
                                        placeholder={t("inventory.fixed_asset_items.modal.base_unit_placeholder", "Select Base Unit")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        disabled
                                        apiType='uom'
                                        customFilter={{
                                            '&template_id.eq': watchedUom || 0,
                                            '&is_base_unit.eq': 1
                                        }}
                                    />
                                </Grid>

                                {/* Depreciation Method */}
                                <Grid item sm={6}>
                                    <DynamicSelect
                                        label={t("inventory.fixed_asset_items.modal.depreciation_method_label", "Depreciation Method")}
                                        name="depreciation_method"
                                        placeholder={t("inventory.fixed_asset_items.modal.depreciation_method_placeholder", "Select Depreciation Method")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        options={DEPRECIATION_METHODS}
                                        required={true}
                                    />
                                </Grid>

                                {/* Declining Factor */}
                                {watchedDepreciationMethod === 'Declining' && (
                                    <Grid item sm={6}>
                                        <DynamicInput
                                            type='number'
                                            formControl={control}
                                            label={t("inventory.fixed_asset_items.modal.declining_factor_label", "Declining Factor")}
                                            name='declining_factor'
                                            fieldArrayName='inventory_fixed_asset_item'
                                            placeholder={t("inventory.fixed_asset_items.modal.declining_factor_placeholder", "Enter Declining Factor")}
                                            required
                                        />
                                    </Grid>
                                )}

                                {/* Computation Method */}
                                <Grid item sm={6}>
                                    <DynamicSelect
                                        label={t("inventory.fixed_asset_items.modal.computation_method_label", "Computation Method")}
                                        name="computation_method"
                                        placeholder={t("inventory.fixed_asset_items.modal.computation_method_placeholder", "Select Computation Method")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        options={COMPUTATION_METHODS}
                                        required={true}
                                    />
                                </Grid>

                                {/* Accounting Section Header */}
                                <Grid item xs={12} pt={2}>
                                    <Typography type='s3' weight='medium' color='theme.neutral.900'>
                                        {t("inventory.fixed_asset_items.modal.accounting_section", "Accounting")}
                                    </Typography>
                                </Grid>

                                {/* Default Tax */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.default_tax_label", "Default Tax")}
                                        name="default_tax_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.default_tax_placeholder", "Select Default Tax")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='taxTemplate'
                                        options={[]}
                                        required={true}
                                    />
                                </Grid>

                                {/* Income Account */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.income_account_label", "Income Account")}
                                        name="income_account_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.income_account_placeholder", "Select Income Account")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='accounts'
                                        options={[]}
                                        required={true}
                                        customeFilter={{ '&account_parent_type.in': ['Income', 'Assets'] }}
                                    />
                                </Grid>

                                {/* Asset Account */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.asset_account_label", "Asset Account")}
                                        name="asset_account_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.asset_account_placeholder", "Select Asset Account")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='accounts'
                                        options={[]}
                                        required={true}
                                        customeFilter={{ '&account_parent_type.eq': 'Assets' }}
                                    />
                                </Grid>

                                {/* COGS Account */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.cogs_account_label", "COGS Account")}
                                        name="cogs_account_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.cogs_account_placeholder", "Select COGS Account")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='accounts'
                                        options={[]}
                                        required={true}
                                    />
                                </Grid>

                                {/* Fixed Asset Account */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.fixed_asset_account_label", "Fixed Asset Account")}
                                        name="fixed_asset_account_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.fixed_asset_account_placeholder", "Select Fixed Asset Account")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='accounts'
                                        options={[]}
                                        required={true}
                                        customeFilter={{ '&account_parent_type.eq': 'Assets' }}
                                    />
                                </Grid>

                                {/* Depreciation Account */}
                                <Grid item sm={6}>
                                    <DynamicSearchSelect
                                        label={t("inventory.fixed_asset_items.modal.depreciation_account_label", "Depreciation Account")}
                                        name="depreciation_account_id"
                                        placeholder={t("inventory.fixed_asset_items.modal.depreciation_account_placeholder", "Select Depreciation Account")}
                                        fieldArrayName='inventory_fixed_asset_item'
                                        formControl={control}
                                        apiType='accounts'
                                        options={[]}
                                        required={true}
                                        customeFilter={{ '&account_parent_type.eq': 'Assets' }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions className='fixedAssetItemAddModal--DialogAction'>
                            <span></span>
                            <Box display='flex' gap={1.5}>
                                <Button
                                    className='fixedAssetItemAddModal--DialogAction--WhiteBtn'
                                    onClick={handleModalClose}>
                                    {t("common.cancel")}
                                </Button>
                                <Button type="button" onClick={handleSubmit(onSubmit)}>{t("common.save")}</Button>
                            </Box>
                        </DialogActions>
                    </form>
                </FormProvider>
            </>
            <Toast
                open={Boolean(toast)}
                type={toast?.type}
                message={toast?.message}
                handleClose={() => setToast(null)}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            />
        </Modal>
    );
};

export default FixedAssetItemAddModal;
